import { useEffect, useState, type ReactNode } from 'react'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { MsalProvider as MsalReactProvider } from '@azure/msal-react'
import { msalConfig, isMsalConfigured } from './msalConfig'

/**
 * Wrapper sobre `@azure/msal-react` que:
 *   1. Crea una sola instancia de `PublicClientApplication`.
 *   2. Espera a `initialize()` antes de renderizar children (requerido por
 *      MSAL Browser ≥ 3).
 *   3. Establece el primer account como activo cuando se complete login.
 *
 * Si Entra aún no está configurado (variables vacías), pasa a un modo de
 * "passthrough" que NO instancia MSAL — útil en Fase A para arrancar la SPA.
 *
 * Tanto la instancia como initialize() viven a nivel de módulo — fuera del
 * ciclo React. React 19 StrictMode monta/desmonta dos veces en dev; si
 * initialize() viviera en useEffect recibiría dos llamadas y MSAL emitiría el
 * warning `logMultipleInstances` (código 1e88vg). Al inicializar en módulo la
 * llamada ocurre exactamente una vez; el useEffect solo se suscribe a la
 * promise ya en vuelo.
 */

export const msalInstance = isMsalConfigured ? new PublicClientApplication(msalConfig) : null
const msalInitPromise: Promise<void> = msalInstance?.initialize() ?? Promise.resolve()

interface MsalProviderProps {
  children: ReactNode
}

export function MsalProvider({ children }: MsalProviderProps) {
  const [ready, setReady] = useState(!msalInstance)

  useEffect(() => {
    if (!msalInstance) return

    let cancelled = false
    msalInitPromise
      .then(() => {
        if (cancelled) return
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(accounts[0] ?? null)
        }
        setReady(true)
      })
      .catch((err) => {
        console.error('[msal] initialize failed', err)
        setReady(true)
      })

    const callbackId = msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        'account' in event.payload &&
        event.payload.account
      ) {
        msalInstance.setActiveAccount(event.payload.account)
      }
    })

    return () => {
      cancelled = true
      if (callbackId) msalInstance.removeEventCallback(callbackId)
    }
  }, [])

  if (!msalInstance) {
    // Modo passthrough: Entra no configurado todavía (Fase A early).
    return <>{children}</>
  }

  if (!ready) {
    return (
      <div className="flex h-full items-center justify-center text-sombra/70">
        Iniciando sesión…
      </div>
    )
  }

  return <MsalReactProvider instance={msalInstance}>{children}</MsalReactProvider>
}
